import {
  Carer,
  Need,
  ResidentItem,
  ResidentNeed,
  ContingencyPlanDTO
} from '@lmc/ts-common'
import { formatAddress, showToast } from 'common/utils'
import dayjs from 'dayjs'
import { capitalize } from 'lodash'
import { RefObject } from 'react'
import {
  ChildrenContainer,
  ExtraText,
  IconContainer,
  LabelContainer
} from '../styledComponents'
import { Text } from '@lmc/ui-kit/src/general'
import {
  EMAR_CARE_RELATED_FIELDS,
  PRO_CARE_RELATED_FIELDS
} from './profileScreenFields'
import { InternalNamePath } from 'antd/lib/form/interface'
import QuestionMarkIcon from '@lmc/ui-kit/src/general/MoreInfoLink/QuestionMarkIcon'
import { Tooltip } from 'antd'
import { Popover, theme } from '@lmc/ui-kit'
import { TooltipTitle } from '../components/EditTooltip'
import DownloadForm from '../components/DownloadForm'
import { BaseOptionType } from 'antd/es/select'
import { shouldDisplayDnacpr } from './dnacpr'
import { NHS_TRACE_STATUS_MAP } from 'common/constants'
import {
  CheckCircleFilled,
  ExclamationCircleFilled,
  InfoCircleFilled
} from '@ant-design/icons'

type CareField = { key: string; label: string; altLabel?: string }

export const dateFormat = 'DD-MM-YYYY'
export const dateFormatTime = 'DD-MM-YYYY HH:mm'
export const NO_INFORMATION_ENTERED = 'No information entered'
const dnacprTooltip = 'Always refer to the original DNACPR form'

export const getResidentInfoDetail = (resident?: ResidentItem) => {
  const name = `${resident?.name?.first} ${resident?.name?.last}`
  const dateOfBirth = dayjs(resident?.dateOfBirth).format('Do MMMM YYYY')
  const age = `(${dayjs().diff(dayjs(resident?.dateOfBirth), 'year')})`
  const gender = capitalize(resident?.gender)

  return `${name} • ${dateOfBirth} ${age} • ${gender}`
}

export const getNHSFormattedNumber = (
  baseValue: string | number,
  isFromCanada?: boolean
) => {
  const value = baseValue.toString()

  if (isFromCanada) {
    return `${value.slice(0, 3)} ${value.slice(3, 6)} ${value.slice(
      6,
      9
    )} ${value.slice(9, 12)}`
  }
  return `${value.slice(0, 3)} ${value.slice(3, 6)} ${value.slice(6)}`
}

export const cleanNHSFormattedNumber = (value?: string) =>
  value?.replaceAll(' ', '')?.replaceAll('_', '')

export const getValue = (
  key: string,
  profile: any,
  carers?: Carer[],
  labelValue?: string,
  isFromCanada?: boolean
) => {
  const value = profile[key]
  const exceptionKey = [
    'dnacpr',
    'dnacprForm',
    'dols',
    'dolsStatus',
    'floor',
    'room',
    'building',
    'bed'
  ]?.includes(key)

  if (labelValue) {
    return labelValue
  }

  if (key === 'carerGender') {
    return profile.carerPreferences?.carerGender
      ? capitalize(profile.carerPreferences.carerGender)
      : NO_INFORMATION_ENTERED
  }

  if (key === 'carerNotes') {
    return profile.carerPreferences?.carerNotes
      ? capitalize(profile.carerPreferences.carerNotes)
      : NO_INFORMATION_ENTERED
  }

  if (!value && !exceptionKey) return NO_INFORMATION_ENTERED

  if (key === 'nhsNumber') {
    return getNHSFormattedNumber(value, isFromCanada)
  }

  if (key === 'languagesSpoken') {
    return value?.length ? value.join(', ') : NO_INFORMATION_ENTERED
  }

  if (key === 'nationalities') {
    return value?.length ? value.join(', ') : NO_INFORMATION_ENTERED
  }

  if (key === 'address') {
    return formatAddress(value)
  }

  if (key === 'name') {
    return `${value.first} ${value.last} ${
      profile?.preferredName ? `(${profile?.preferredName})` : ''
    }`
  }

  if (key === 'dateOfBirth') {
    return `${value} ${`(${dayjs().diff(dayjs(value), 'years')})`}`
  }

  if (key === 'deathDate') {
    // Format to match dateOfBirth display format
    return value ? dayjs(value).format('YYYY-MM-DD') : NO_INFORMATION_ENTERED
  }

  if (key === 'dnacpr') {
    if (value === null) {
      return 'No information entered '
    }

    const dnacprStartDate = profile['dnacprStartDate']
    const displayDnacpr = shouldDisplayDnacpr(value, dnacprStartDate)

    return `${displayDnacpr ? 'Do Not Attempt CPR' : 'Attempt CPR'} `
  }
  if (key === 'dnacprForm') {
    if (profile.dnacpr && (value || profile.dnacprFormPhysicalLocation)) {
      return value ? (
        <DownloadForm
          fileUrl={profile.dnacprForm}
          fileName={`${profile.name.first} ${profile.name.last}'s DNACPR Form`}
        >
          <Text weight="medium">View Form</Text>
        </DownloadForm>
      ) : (
        <Text weight="medium">Form not uploaded</Text>
      )
    }
    return NO_INFORMATION_ENTERED
  }

  if (key === 'dols') {
    return `${value ? 'Yes' : 'No'} `
  }

  if (key === 'dolsStatus') {
    const value = profile['dolsStatus']
    return !value ? 'No Status' : capitalize(value)
  }

  if (key === 'admissionDate') {
    return `${dayjs(value).format('Do MMMM YYYY')}`
  }

  if (key === 'zone') {
    return `${profile?.zoneTitle}`
  }

  if (key === 'building') {
    return `${profile?.location?.building || NO_INFORMATION_ENTERED}`
  }

  if (key === 'floor') {
    return `${profile?.location?.floor || NO_INFORMATION_ENTERED}`
  }

  if (key === 'room') {
    return `${profile?.location?.room || NO_INFORMATION_ENTERED}`
  }

  if (key === 'bed') {
    return `${profile?.location?.bed || NO_INFORMATION_ENTERED}`
  }

  if (key === 'carers') {
    return `${
      profile?.assignAll
        ? 'All carers'
        : profile?.carers
            ?.map((carerId: string) => {
              const item = carers?.find((carer) => carer?.id === carerId)?.name
              return item?.full || `${item?.first} ${item?.last}`
            })
            ?.join(', ')
    }`
  }

  if (key === 'keyworkers') {
    if (profile?.keyworkers?.length === 0) {
      return NO_INFORMATION_ENTERED
    }

    return `${profile?.keyworkers
      ?.map((keyworker: string) => {
        const item = carers?.find((carer) => carer?.id === keyworker)?.name
        return item?.full || `${item?.first} ${item?.last}`
      })
      ?.join(', ')}`
  }

  if (key === 'medicalHistory') {
    return profile[key]
  }

  return capitalize(profile[key])
}

const NhsTraceStatusIcon = ({
  iconType,
  iconColor
}: {
  iconType: string
  iconColor: string
}) => {
  const style = { color: iconColor, fontSize: 14 }
  switch (iconType) {
    case 'check':
      return <CheckCircleFilled style={style} />
    case 'error':
      return <ExclamationCircleFilled style={style} />
    case 'info':
    default:
      return <InfoCircleFilled style={style} />
  }
}

export const getNhsTraceStatusExtra = (
  profile: any,
  statusInfoLabel = 'Status info'
) => {
  const traceStatusCode = profile?.nhsNumberTraceStatus
  if (!traceStatusCode) return undefined
  const traceStatus = NHS_TRACE_STATUS_MAP[traceStatusCode]
  if (!traceStatus) return undefined

  return (
    <span
      style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 8 }}
    >
      <NhsTraceStatusIcon
        iconType={traceStatus.iconType}
        iconColor={traceStatus.iconColor}
      />
      <span style={{ color: theme.color.neutral.color3 }}>
        {traceStatus.shortLabel}
      </span>
      <Popover
        trigger={['hover', 'click']}
        overlayStyle={{ maxWidth: 500 }}
        content={
          <>
            <Text>{traceStatus.tooltip}</Text>
            {traceStatus.popoverLink && (
              <a
                href={traceStatus.popoverLink.url}
                target="_blank"
                rel="noreferrer"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  marginTop: 4
                }}
              >
                {traceStatus.popoverLink.label} ›
              </a>
            )}
          </>
        }
      >
        <span
          style={{
            color: theme.color.hover.primary,
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 2
          }}
        >
          {statusInfoLabel}
          <IconContainer>
            <QuestionMarkIcon color={theme.color.hover.primary} />
          </IconContainer>
        </span>
      </Popover>
    </span>
  )
}

export const getExtra = (
  key: string,
  profile: ResidentItem,
  statusInfoLabel?: string
) => {
  if (key === 'dnacpr') {
    return profile?.dnacprReviewDate
      ? `Review Date: ${dayjs(profile?.dnacprReviewDate).format('DD/MM/YYYY')}`
      : profile?.dnacprDetails
  }

  if (key === 'dnacprForm') {
    return profile?.dnacprFormPhysicalLocation
  }

  if (key === 'dols') {
    const today = dayjs().startOf('day')
    const inputDate = dayjs(profile?.dolsReviewDate).startOf('day')
    const isOverdue = inputDate.isBefore(today)
    if (isOverdue) {
      return 'Review date overdue'
    }
    return profile?.dolsReviewDate
      ? `Review Date: ${dayjs(profile?.dolsReviewDate).format('DD/MM/YYYY')}`
      : 'No date set'
  }

  if (key === 'nhsNumber') {
    return getNhsTraceStatusExtra(profile, statusInfoLabel)
  }

  return undefined
}

export const getProfileViewSectionItems = (
  items: { key: string; label: string; labelValue?: string }[],
  resident: ResidentItem,
  carers?: Carer[],
  isFromCanada?: boolean,
  keyworkersEnabled?: boolean,
  statusInfoLabel?: string
) => {
  const resultItems = items
    ?.filter((item) => {
      if (item.key === 'keyworkers' && !keyworkersEnabled) {
        return false
      }
      return true
    })
    ?.map((field, i) => {
      return {
        key: i,
        label: (
          <LabelContainer>
            <Text>{field?.label}</Text>
            {field.key === 'dnacpr' && (
              <Tooltip
                color={theme.color.neutral.color3}
                placement="topLeft"
                title={<TooltipTitle>{dnacprTooltip}</TooltipTitle>}
              >
                <IconContainer>
                  <QuestionMarkIcon />
                </IconContainer>
              </Tooltip>
            )}
          </LabelContainer>
        ),
        children:
          getValue(
            field.key,
            resident,
            carers,
            field.labelValue || undefined,
            isFromCanada
          ) === NO_INFORMATION_ENTERED ? (
            NO_INFORMATION_ENTERED
          ) : (
            <ChildrenContainer>
              <Text>
                {getValue(
                  field.key,
                  resident,
                  carers,
                  field.labelValue || undefined,
                  isFromCanada
                )}
              </Text>
              {getExtra(field.key, resident, statusInfoLabel) && (
                <ExtraText level={2}>
                  {getExtra(field.key, resident, statusInfoLabel)}
                </ExtraText>
              )}
            </ChildrenContainer>
          )
      }
    })
    .filter((field) => field.children !== NO_INFORMATION_ENTERED)
  return resultItems
}

export const getCareRelatedFields = (
  isPro: boolean,
  isEmar: boolean,
  isFromCanada: boolean,
  isDoLSOverride: boolean,
  hasRota: boolean,
  dolsModsAlignment: boolean = false
): CareField[] => {
  let careRelatedFields: CareField[] = []

  const proFields = PRO_CARE_RELATED_FIELDS?.filter((item) =>
    hasRota && isPro
      ? !['fundingSource', 'fundingSourceOther']?.includes(item.key)
      : true
  ).filter((field) => {
    return dolsModsAlignment && field.key === 'dols' ? false : true
  })

  if (isPro && isEmar) {
    careRelatedFields = [...proFields, ...EMAR_CARE_RELATED_FIELDS]
  } else if (isPro) {
    careRelatedFields = proFields
  } else if (isEmar) {
    careRelatedFields = EMAR_CARE_RELATED_FIELDS
  }

  if (isDoLSOverride) {
    careRelatedFields = careRelatedFields.map((field) =>
      field.key === 'dols' ? { key: 'dols', label: 'DoLS' } : field
    )
  }

  if (isDoLSOverride) {
    careRelatedFields = careRelatedFields.map((field) =>
      field.key === 'dolsStatus'
        ? { key: 'dolsStatus', label: 'DoLS Status' }
        : field
    )
  }

  if (isFromCanada) {
    careRelatedFields = careRelatedFields
      .filter((field) => field.key !== 'nationalInsurance')
      .map((field) =>
        field.key === 'nhsNumber'
          ? { key: 'nhsNumber', label: 'Health Care Number' }
          : field
      )
  }

  return careRelatedFields
}

export const getPhoneLabelFromValue = (value: string | undefined) => {
  if (!value) {
    return 'New phone number'
  }
  return `${value.charAt(0).toUpperCase()}${value.slice(1)}`
}

export const scrollToTop = (topRef: RefObject<HTMLElement>) => {
  if (topRef?.current) {
    topRef?.current?.scrollIntoView({ behavior: 'instant' })
  }
}

export const scrollToSection = (sectionRef: RefObject<HTMLElement>) => {
  if (sectionRef?.current) {
    sectionRef?.current?.scrollIntoView({ behavior: 'smooth' })
  }
}

export const toFeetAndInches = (cm: number) => {
  // 1 foot is 30.48 cm, and 1 inch is 2.54 cm
  const feet = Math.floor(cm / 30.48)
  const inches = Math.round((cm - feet * 30.48) / 2.54)

  return `${feet}'${inches}"`
}

export const getAge = (residentAge: string | undefined) => {
  if (residentAge === undefined) {
    return undefined
  }
  const birthDate = dayjs(residentAge)
  const age = dayjs().diff(birthDate, 'year')
  return age
}

export const getSelectOptions = (
  options: BaseOptionType[]
): BaseOptionType[] => {
  return options.map((option) => ({
    ...option,
    value: option.value,
    label: <Text>{option.label}</Text>
  }))
}

export const getSelectOptionsWithTooltip = (
  options: BaseOptionType[]
): BaseOptionType[] => {
  const selectOptions = getSelectOptions(options)

  const optionsWithTooltip = selectOptions.map((option) => ({
    ...option,
    label: (
      <Tooltip placement="top" title={option.label}>
        {option.label}
      </Tooltip>
    )
  }))

  return optionsWithTooltip
}

export const getNeedsNames = (
  residentNeeds: ResidentNeed[] | undefined,
  allNeeds: Need[] | undefined
) => {
  if (!residentNeeds || !allNeeds) {
    return undefined
  }
  return residentNeeds.map((residentNeed) => {
    const need = allNeeds.find((need) => need.id === residentNeed.need)
    return need ? need.name : null
  })
}

export const handleOnFinishFailed = (
  errorFields: {
    name: InternalNamePath
    errors: string[]
  }[]
) =>
  errorFields.map((errorField) =>
    errorField.errors.map((err) => showToast({ title: err, type: 'error' }))
  )

export const getPlanSubtitle = (plan: ContingencyPlanDTO) => {
  return `${plan.editedOn ? `Reviewed ${dayjs(plan.editedOn).format('DD-MM-YYYY')} • ` : ''}${
    plan.editedBy?.name || plan.createdBy?.name
  } • Next review ${dayjs(plan.nextReviewDate).format('DD-MM-YYYY')}`
}

export const isPlanOverdue = (planDate: string) => {
  const inputDate = dayjs(planDate).startOf('day')
  return inputDate.isBefore(dayjs())
}

export const matchesName = (
  inputName: string,
  nameToMatch: string
): boolean => {
  // Remove spaces and convert both strings to lowercase for case-insensitive comparison
  const normalizedInputName = inputName.replace(/\s+/g, '').toLowerCase()
  const normalizedNameToMatch = nameToMatch.replace(/\s+/g, '').toLowerCase()

  return normalizedInputName === normalizedNameToMatch
}
